import React, { useRef, useState } from 'react';
import { unifiedTasksApi } from '../../api/unifiedTasks';
import { Button } from '../../components/ui';
import './lanes.css';

/**
 * "Import tasks" dialog, extracted from the legacy Tasks.tsx (Tasks Rebuild
 * Task 12) -- a direct, self-contained port of its
 * dragActive/importFile/importInProgress/importResults/autoCompleteTasks
 * state + handleDrag/handleDrop/handleFileSelect/handleImportTasks/
 * resetImportDialog logic and the "Import Dialog" JSX block. Replaces
 * LaneShell's `handleImportStub` `window.alert` placeholder (T11 TODO).
 *
 * CSV format (unchanged from legacy): two columns, `task_id` and
 * `claim_reference`; the `autoComplete` toggle controls whether a
 * successfully-assigned task is also marked complete.
 */

export interface ImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful import (imported > 0) so the caller can
   * refresh lane rows/counts -- same contract as CreateTaskModal's onCreated. */
  onImported?: () => void;
  showToast?: (toast: { type: 'success' | 'error' | 'info'; message: string }) => void;
}

interface ImportResults {
  imported?: number;
  action?: 'completed' | 'assigned';
  leftOpenPosts?: number;
  skippedTerminalPosts?: number;
  errors?: Array<{ row: number; error: string }>;
}

export const ImportDialog: React.FC<ImportDialogProps> = ({ isOpen, onClose, onImported, showToast }) => {
  const [dragActive, setDragActive] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importInProgress, setImportInProgress] = useState(false);
  const [importResults, setImportResults] = useState<ImportResults | null>(null);
  const [autoCompleteTasks, setAutoCompleteTasks] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fileInputId = useRef(`import-file-input-${Math.random().toString(36).slice(2)}`).current;

  const reset = () => {
    setDragActive(false);
    setImportFile(null);
    setImportInProgress(false);
    setImportResults(null);
    setAutoCompleteTasks(true);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const acceptFile = (file: File) => {
    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      setImportFile(file);
      setError(null);
    } else {
      setError('Please upload a CSV file');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) acceptFile(e.dataTransfer.files[0]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) acceptFile(e.target.files[0]);
  };

  const handleImportTasks = async () => {
    if (!importFile) return;
    try {
      setImportInProgress(true);
      setImportResults(null);
      setError(null);
      const result = await unifiedTasksApi.importTasks(importFile, autoCompleteTasks);
      const imported = result.imported ?? (typeof result.success === 'number' ? result.success : 0);
      const leftOpenPosts = result.leftOpenPosts?.length || 0;
      const skippedTerminalPosts = result.skippedTerminalPosts?.length || 0;
      if (result.success || leftOpenPosts || skippedTerminalPosts) {
        const action = result.action || (autoCompleteTasks ? 'auto-completed' : 'assigned');
        setImportResults({ imported, leftOpenPosts, skippedTerminalPosts, errors: result.errors,
          action: action === 'assigned' ? 'assigned' : 'completed' });
        if (imported > 0) {
          showToast?.({ type: 'success', message: `Successfully ${action} ${imported} task${imported > 1 ? 's' : ''}.` });
        } else if (!leftOpenPosts && !skippedTerminalPosts && result.errors && result.errors.length > 0) {
          showToast?.({ type: 'error', message: `Import failed: all ${result.errors.length} task${result.errors.length > 1 ? 's' : ''} had errors.` });
        }
        if (imported || leftOpenPosts) onImported?.();
      } else {
        setError(result.message || 'Import failed');
      }
    } catch (e) {
      console.error('Error importing tasks:', e);
      setError('Failed to import tasks');
    } finally {
      setImportInProgress(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="lane-modal-backdrop" onClick={handleClose}>
      <div className="lane-modal lane-modal--wide" onClick={(e) => e.stopPropagation()}>
        <h2 className="lane-modal__title">Import Tasks</h2>

        {error && <div className="lane-modal__error">{error}</div>}

        <div className="import-dialog__instructions">
          <h4>CSV format requirements</h4>
          <ul>
            <li>Two columns: <code>task_id</code> and <code>claim_reference</code></li>
            <li>Tasks will be automatically assigned to claims</li>
            <li>Optional: control completion with the toggle below</li>
          </ul>
        </div>

        <label className="import-dialog__auto-complete">
          <input
            type="checkbox"
            checked={autoCompleteTasks}
            onChange={(e) => setAutoCompleteTasks(e.target.checked)}
          />
          <div>
            <span className="import-dialog__auto-complete-title">Auto-complete tasks after assignment</span>
            <p className="import-dialog__auto-complete-hint">
              {autoCompleteTasks
                ? 'Tasks will be marked as completed after assignment. Post tasks remain open until their documents are filed.'
                : 'Tasks will be assigned but remain open for review'}
            </p>
          </div>
        </label>

        <div
          className={`import-dialog__dropzone ${dragActive ? 'import-dialog__dropzone--active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !importFile && document.getElementById(fileInputId)?.click()}
        >
          {importFile ? (
            <div>
              <p className="import-dialog__file-name">{importFile.name}</p>
              <p className="import-dialog__file-meta">{(importFile.size / 1024).toFixed(2)} KB &bull; CSV file</p>
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => { e.stopPropagation(); setImportFile(null); setImportResults(null); }}
              >
                Remove file
              </Button>
            </div>
          ) : (
            <div>
              <p className="import-dialog__drop-title">Drop your CSV file here</p>
              <p className="import-dialog__drop-hint">or browse files</p>
            </div>
          )}
          <input id={fileInputId} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />
        </div>

        {importInProgress && <div className="lane-modal__hint">Importing tasks...</div>}

        {importResults && (
          <div className="import-dialog__results">
            {!!importResults.skippedTerminalPosts && (
              <p>{importResults.skippedTerminalPosts} post task{importResults.skippedTerminalPosts > 1 ? 's' : ''} skipped because {importResults.skippedTerminalPosts > 1 ? 'they are' : 'it is'} no longer open. Status and assignment unchanged.</p>
            )}
            {!!importResults.leftOpenPosts && (
              <p>{importResults.leftOpenPosts} post task{importResults.leftOpenPosts > 1 ? 's' : ''} assigned and left open. Use Mark Completed to file each document on its claim.</p>
            )}
            {importResults.imported != null && importResults.imported > 0 && (
              <div className="import-dialog__success">
                Successfully {importResults.action} {importResults.imported} task{importResults.imported > 1 ? 's' : ''}.
              </div>
            )}
            {importResults.errors && importResults.errors.length > 0 && (
              <div className="import-dialog__errors">
                <p>Import errors ({importResults.errors.length}):</p>
                <div className="import-dialog__error-list">
                  {importResults.errors.map((err, i) => (
                    <div key={i}>Row {err.row}: {err.error}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="lane-modal__actions">
          <Button variant="secondary" onClick={handleClose}>{importResults ? 'Close' : 'Cancel'}</Button>
          {importFile && !importInProgress && !importResults && (
            <Button variant="primary" onClick={handleImportTasks}>Import</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportDialog;
